import { Component, computed, effect, inject, input, linkedSignal, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { type Sort } from '@angular/material/sort';
import { RouterLink } from '@angular/router';
import { type SortItemDto } from '@api/incidents';
import { fileUtils, SocketEventNamespace } from '@core';
import { IncidentTabStatusFilter } from '@models';
import { SocketEvents } from '@ngrx-store';
import { AvsDatagridComponent, DatagridCellDirective } from '@ui';
import { filter } from 'rxjs';

import { IncidentsCriteriaStore, type IncidentsFilterCriteria } from '../../../state/incidents-criteria.store';
import { IncidentSeverityIconComponent } from '../../incident-status';
import { NotesDialogComponent } from '../../notes-dialog';
import { buildIncidentsV2GridColumns, type IncidentsV2GridRow, toIncidentsV2GridRows } from './incidents-v2-grid';
import { IncidentsV2TableStore } from './incidents-v2-table.store';

const LOAD_MORE_THRESHOLD_PX = 200;

@Component({
  imports: [
    AvsDatagridComponent,
    DatagridCellDirective,
    IncidentSeverityIconComponent,
    MatIcon,
    MatProgressSpinner,
    RouterLink,
  ],
  providers: [IncidentsV2TableStore],
  selector: 'app-incidents-v2-table',
  styleUrl: './incidents-v2-table.component.scss',
  templateUrl: './incidents-v2-table.component.html',
})
export class IncidentsV2TableComponent {
  criteria = input.required<IncidentsFilterCriteria>();
  tab = input.required<IncidentTabStatusFilter>();
  sortCriteria = input<SortItemDto | null>(null);
  columns = input<string[] | null>(null);
  noDataText = input('Data is Empty');
  noResolvedIncidentsText = input('No resolved incidents for selected period');
  isNoDataBigSize = input(false);
  isMobile = input(false);
  isInjected = input(false);
  tableId = input('');

  readonly store = inject(IncidentsV2TableStore);
  readonly criteriaStore = inject(IncidentsCriteriaStore);

  protected readonly loadMoreThreshold = LOAD_MORE_THRESHOLD_PX;
  protected readonly rows = computed(() => toIncidentsV2GridRows(this.store.rows()));
  protected readonly gridColumns = computed(() => buildIncidentsV2GridColumns(this.isMobile()));
  protected readonly columnOrder = computed(() => this.columns() ?? this.criteriaStore.displayedColumns());
  protected readonly stickyColumns = computed(() => !this.isMobile());
  protected readonly fixedScroll = computed(() => this.isMobile() || this.isInjected());
  protected readonly gridTableId = computed(() => this.tableId() || null);
  protected readonly noDataCopy = computed(() => (this.tab() === IncidentTabStatusFilter.HISTORY
    ? this.noResolvedIncidentsText()
    : this.noDataText()));
  protected readonly isInitialLoad = signal(true);
  protected readonly isTabSwitching = signal(false);
  protected readonly showSpinner = computed(() => this.store.isLoading() && (this.isInitialLoad() || this.isTabSwitching()));
  protected readonly gridDisplay = computed(() => (this.showSpinner() ? 'none' : null));
  protected readonly gridSort = computed<Sort | null>(() => {
    const sort = this.activeSortCriteria();

    return sort
      ? {
        active: sort.field,
        direction: sort.order,
      }
      : null;
  });

  private readonly dialog = inject(MatDialog);
  private readonly activeSortCriteria = linkedSignal(() => this.sortCriteria());

  constructor() {
    let previousTab: IncidentTabStatusFilter | undefined;

    effect(() => {
      const criteria = this.criteria();
      const tab = this.tab();
      const sort = this.activeSortCriteria();

      if (previousTab !== undefined && previousTab !== tab) {
        this.isTabSwitching.set(true);
      }

      previousTab = tab;

      untracked(() => this.store.setQuery({
        criteria,
        sort,
        tab,
      }));
    });

    effect(() => {
      if (!this.store.isLoading()) {
        this.isInitialLoad.set(false);
        this.isTabSwitching.set(false);
      }
    });

    SocketEvents
      .pipe(
        filter(({ type }) => type.startsWith(SocketEventNamespace.INCIDENTS)),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.store.reload());
  }

  prepareIncidentsDataForDownloading() {
    const columns = this.columnOrder().map(column => (column === 'index' ? 'id' : column));

    return fileUtils.prepareDataForDownloading(columns, this.rows());
  }

  exportIncidentsToCSV() {
    fileUtils.exportDataToCSV(this.prepareIncidentsDataForDownloading(), 'incidents report');
  }

  exportIncidentsToJSON() {
    fileUtils.exportDataToJSON(this.prepareIncidentsDataForDownloading(), 'incidents report');
  }

  protected onSortChange(sort: Sort) {
    const sortCriteria = sort.direction
      ? {
        field: sort.active,
        order: sort.direction,
      }
      : null;

    this.activeSortCriteria.set(sortCriteria);
    this.criteriaStore.setSortCriteria(sortCriteria);
  }

  protected trackById(_index: number, row: IncidentsV2GridRow) {
    return row.id;
  }

  protected openNotesDialog(incident: IncidentsV2GridRow) {
    this.dialog.open(NotesDialogComponent, {
      backdropClass: 'backdrop-modal-panel',
      data: { incident },
    });
  }
}
